import {
  addDays,
  endOfMinute,
  format,
  isEqual,
  set,
  startOfDay,
  subDays,
  subMilliseconds,
} from "date-fns";
import type { ReceiptFilters } from "../api/receiptsApi";
import type { ReceiptType } from "@KassaroAB/shared/types/enums/receiptType";
import type { StaffId } from "@KassaroAB/shared/types/ids";

export type DatePreset = {
  id: string;
  daysAgo: number;
  labelKey?: "today" | "yesterday";
};

export const datePresets: DatePreset[] = [
  { id: "today", daysAgo: 0, labelKey: "today" },
  { id: "yesterday", daysAgo: 1, labelKey: "yesterday" },
  { id: "daysAgo-2", daysAgo: 2 },
  { id: "daysAgo-3", daysAgo: 3 },
  { id: "daysAgo-4", daysAgo: 4 },
];

const businessDayStart = (date: Date, businessDayStartHour: number) =>
  set(startOfDay(date), { hours: businessDayStartHour });

const businessDayEnd = (date: Date, businessDayStartHour: number) =>
  subMilliseconds(businessDayStart(addDays(date, 1), businessDayStartHour), 1);

const businessDateOf = (instant: Date, businessDayStartHour: number) =>
  startOfDay(
    instant.getHours() < businessDayStartHour ? subDays(instant, 1) : instant,
  );

export const getPresetDate = (
  daysAgo: number,
  businessDayStartHour: number,
) => {
  return subDays(businessDateOf(new Date(), businessDayStartHour), daysAgo);
};

export const getPresetLabel = (
  preset: DatePreset,
  t: (key: string) => string,
  businessDayStartHour: number,
): string => {
  if (preset.labelKey === "today") {
    return t("receiptsView.filter.presetToday");
  }
  if (preset.labelKey === "yesterday") {
    return t("receiptsView.filter.presetYesterday");
  }
  return format(getPresetDate(preset.daysAgo, businessDayStartHour), "d MMM");
};

export const initFromFilters = (
  businessDayStartHour: number,
  startIso?: string,
  endIso?: string,
): {
  date?: Date;
  startTime?: string;
  endTime?: string;
} => {
  if (!startIso && !endIso) {
    return {};
  }
  const startParsed = startIso ? new Date(startIso) : undefined;
  const endParsed = endIso ? new Date(endIso) : undefined;
  const date = businessDateOf(startParsed ?? endParsed!, businessDayStartHour);
  let startTime: string | undefined;
  let endTime: string | undefined;
  if (
    startParsed &&
    !isEqual(startParsed, businessDayStart(date, businessDayStartHour))
  ) {
    startTime = format(startParsed, "HH:mm");
  }
  if (
    endParsed &&
    !isEqual(endParsed, businessDayEnd(date, businessDayStartHour))
  ) {
    endTime = format(endParsed, "HH:mm");
  }
  return { date, startTime, endTime };
};

const parseTime = (raw: string): { hour: number; minute: number } => {
  const t = raw.trim();
  const match = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!match) {
    return { hour: 9, minute: 0 };
  }
  return { hour: Number(match[1]), minute: Number(match[2]) };
};

const applyTime = (date: Date, time: string, businessDayStartHour: number) => {
  const { hour, minute } = parseTime(time);
  const day = hour < businessDayStartHour ? addDays(date, 1) : date;
  return set(startOfDay(day), {
    hours: hour,
    minutes: minute,
    seconds: 0,
    milliseconds: 0,
  });
};

export const buildDateRange = ({
  date,
  startTime,
  endTime,
  businessDayStartHour,
}: {
  date: Date;
  startTime?: string;
  endTime?: string;
  businessDayStartHour: number;
}) => {
  const rangeStart = startTime
    ? applyTime(date, startTime, businessDayStartHour)
    : businessDayStart(date, businessDayStartHour);
  let rangeEnd = endTime
    ? endOfMinute(applyTime(date, endTime, businessDayStartHour))
    : businessDayEnd(date, businessDayStartHour);
  if (rangeStart > rangeEnd && endTime) {
    rangeEnd = addDays(rangeEnd, 1);
  }
  return {
    startDate: rangeStart.toISOString(),
    endDate: rangeEnd.toISOString(),
  };
};

export const buildReceiptFilters = ({
  selectedType,
  selectedStaffId,
  date,
  startTime,
  endTime,
  businessDayStartHour,
}: {
  selectedType?: ReceiptType;
  selectedStaffId?: StaffId;
  date?: Date;
  startTime?: string;
  endTime?: string;
  businessDayStartHour: number;
}): ReceiptFilters | undefined => {
  const appliedFilters: ReceiptFilters = {};
  if (selectedType) {
    appliedFilters.type = selectedType;
  }
  if (selectedStaffId) {
    appliedFilters.staffId = selectedStaffId;
  }
  if (date) {
    const range = buildDateRange({
      date,
      startTime,
      endTime,
      businessDayStartHour,
    });
    appliedFilters.startDate = range.startDate;
    appliedFilters.endDate = range.endDate;
  }

  const hasFilters = Object.keys(appliedFilters).length > 0;
  return hasFilters ? appliedFilters : undefined;
};

export const formatDateForInput = (date: Date | undefined): string => {
  if (!date) return "";
  return format(date, "yyyy-MM-dd");
};

export const parseDateInput = (value: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return startOfDay(parsed);
};

export const todayMaxDate = (): string => format(new Date(), "yyyy-MM-dd");
